import React, { useState, useEffect } from 'react';
import { 
  ArrowRight, 
  CheckCircle, 
  Shield, 
  Lock, 
  AlertTriangle, 
  Info, 
  LayoutTemplate,
  Users,
  Settings,
  FileCode,
  Check,
  Building2,
  Globe,
  Bug,
  Activity,
  FileText
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from './ui/card';
import { Button } from './ui/button';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { formatCategoryLabel } from '../utils/normalizeAcronyms';

export interface SpaceArchitecture {
  spaceType: 'jsm' | 'jira' | 'hybrid';
  audience: { internal: boolean; external: boolean };
  needs: { portal: boolean; slas: boolean; incidents: boolean; changes: boolean };
  configStrategy: 'shared' | 'dedicated' | 'shared_with_exceptions';
  includedWorkTypes: string[];
  recommendationText: string;
  decidedByName?: string;
  status: 'draft' | 'ready_for_build' | 'approved';
  locked: boolean;
  lockedAt?: string;
  reopenDetails?: { classification: 'defect' | 'scope_change'; reason: string };
}

interface SpaceArchitectureWizardProps {
  initialData?: SpaceArchitecture | null;
  onSave: (data: SpaceArchitecture) => void;
  onCancel: () => void;
  userRole: 'client' | 'consultant' | 'admin';
}

export function SpaceArchitectureWizard({ 
  initialData, 
  onSave, 
  onCancel,
  userRole 
}: SpaceArchitectureWizardProps) {
  const [step, setStep] = useState(1);
  const [data, setData] = useState<SpaceArchitecture>(initialData || {
    spaceType: 'jsm', // default
    audience: { internal: true, external: false },
    needs: { portal: false, slas: false, incidents: false, changes: false },
    configStrategy: 'shared',
    includedWorkTypes: [],
    recommendationText: '',
    status: 'draft',
    locked: false
  });

  // Local state for wizard flow (mapped to data structure)
  const [goal, setGoal] = useState<'itsm' | 'software' | 'both'>('itsm');
  const [audienceInternal, setAudienceInternal] = useState(true);
  const [audienceExternal, setAudienceExternal] = useState(false);
  
  const [needPortal, setNeedPortal] = useState(false);
  const [needSlas, setNeedSlas] = useState(false);
  const [needIncidents, setNeedIncidents] = useState(false);
  const [needChanges, setNeedChanges] = useState(false);
  
  const [multiTeamDiff, setMultiTeamDiff] = useState<'no' | 'yes' | 'unsure'>('no');

  const [showCommitDialog, setShowCommitDialog] = useState(false);
  const [showUnlockDialog, setShowUnlockDialog] = useState(false);

  // Initialize state from existing data if present
  useEffect(() => {
    if (initialData) {
        setData(initialData);
        if (initialData.locked) {
            setStep(5); // Jump to summary if locked
        }
        
        // Reverse engineer local state for editing (if unlocked)
        // Note: In a real app, we might store these explicit answers too.
        // For MVP, we infer or just start fresh if drafting.
        setAudienceInternal(initialData.audience.internal);
        setAudienceExternal(initialData.audience.external);
        setNeedPortal(initialData.needs.portal);
        setNeedSlas(initialData.needs.slas);
        setNeedIncidents(initialData.needs.incidents);
        setNeedChanges(initialData.needs.changes);
        
        if (initialData.spaceType === 'hybrid') setGoal('both');
        else if (initialData.spaceType === 'jira') setGoal('software');
        else setGoal('itsm');
        
        if (initialData.configStrategy === 'dedicated') setMultiTeamDiff('yes');
        else if (initialData.configStrategy === 'shared_with_exceptions') setMultiTeamDiff('unsure');
        else setMultiTeamDiff('no');
    }
  }, [initialData]);

  // Logic engine to determine recommendation
  useEffect(() => {
      // 1. Determine Space Type
      let recSpaceType: 'jsm' | 'jira' | 'hybrid' = 'jsm';
      if (goal === 'both') recSpaceType = 'hybrid';
      else if (goal === 'software') recSpaceType = 'jira';
      
      // Override: If portal or SLAs are strictly needed, JSM is required even if goal was software (hybrid)
      if (goal === 'software' && (needPortal || needSlas)) {
          recSpaceType = 'hybrid';
      }

      // 2. Determine Strategy
      let recStrategy: 'shared' | 'dedicated' | 'shared_with_exceptions' = 'shared';
      if (multiTeamDiff === 'yes') recStrategy = 'dedicated';
      if (multiTeamDiff === 'unsure') recStrategy = 'shared_with_exceptions';

      // 3. Determine Work Types
      const works: string[] = [];
      if (recSpaceType === 'jsm' || recSpaceType === 'hybrid') {
          works.push('Service Request');
          if (needIncidents) {
              works.push('Incident');
              works.push('Post-Incident Review');
              works.push('Problem');
          }
          if (needChanges) {
              works.push('Change');
          }
      }
      // Always have SR as baseline if nothing else
      if (works.length === 0) works.push('Service Request');

      // 4. Generate Text
      let text = `Based on your needs, we recommend a ${
          recSpaceType === 'jsm' ? 'Jira Service Management Space (ITSM)' : 
          recSpaceType === 'jira' ? 'Jira Software Project' : 
          'Hybrid Architecture (JSM + Jira Software)'
      }. `;
      
      text += `We suggest starting with a ${formatCategoryLabel(recStrategy)} configuration strategy `;
      text += recStrategy === 'shared' ? 'to maximize speed and consistency.' : 'to accommodate unique team requirements.';

      setData(prev => ({
          ...prev,
          spaceType: recSpaceType,
          configStrategy: recStrategy,
          includedWorkTypes: works,
          recommendationText: text,
          audience: { internal: audienceInternal, external: audienceExternal },
          needs: { portal: needPortal, slas: needSlas, incidents: needIncidents, changes: needChanges }
      }));

  }, [goal, audienceInternal, audienceExternal, needPortal, needSlas, needIncidents, needChanges, multiTeamDiff]);

  const handleNext = () => setStep(step + 1);
  const handleBack = () => setStep(step - 1);

  const canProceedStep1 = true;
  const canProceedStep2 = audienceInternal || audienceExternal;
  const canProceedStep3 = true;
  const canProceedStep4 = true;

  const handleCommit = (signerName: string) => {
      const lockedData: SpaceArchitecture = {
          ...data,
          status: 'ready_for_build',
          locked: true,
          lockedAt: new Date().toISOString(),
          decidedByName: signerName
      };
      onSave(lockedData);
      toast.success("Space Architecture Decision Locked");
  };

  const handleUnlock = (classification: 'defect' | 'scope_change', reason: string) => {
      const unlockedData: SpaceArchitecture = {
          ...data,
          status: 'draft',
          locked: false,
          reopenDetails: { classification, reason }
      };
      setData(unlockedData); // Local update to unlock view
      setStep(1); // Go back to start
      onSave(unlockedData); // Persist unlock state
      setShowUnlockDialog(false);
      toast.success(`Unlocked as ${classification === 'scope_change' ? 'Scope Change' : 'Defect Fix'}`);
  };

  if (step === 5 || data.locked) {
      // Summary / Locked View
      return (
          <div className="h-full flex flex-col">
               <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    <Card className={`border-l-4 ${data.locked ? 'border-l-blue-500' : 'border-l-amber-400'}`}>
                        <CardHeader>
                             <div className="flex items-center justify-between">
                                 <div>
                                     <CardTitle>Architecture Decision Record</CardTitle>
                                     <CardDescription>
                                         {data.locked 
                                            ? `Decided by ${data.decidedByName} on ${new Date(data.lockedAt!).toLocaleDateString()}`
                                            : "Review recommendations before committing."}
                                     </CardDescription>
                                 </div>
                                 <Badge variant={data.locked ? 'default' : 'outline'}>
                                     {data.locked ? <Lock className="w-3 h-3 mr-1" /> : <AlertTriangle className="w-3 h-3 mr-1" />}
                                     {data.status.toUpperCase().replace(/_/g, ' ')}
                                 </Badge>
                             </div>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                                <h3 className="text-sm font-semibold uppercase text-slate-500 mb-2">Primary Recommendation</h3>
                                <p className="text-lg font-medium text-slate-900">{data.recommendationText}</p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="border rounded-md p-4">
                                     <div className="flex items-center gap-2 mb-2 font-semibold text-slate-700">
                                         <LayoutTemplate className="w-4 h-4" /> Space Type
                                     </div>
                                     <div className="text-2xl font-bold text-blue-700">
                                         {data.spaceType === 'jsm' ? 'JSM (ITSM)' : data.spaceType === 'jira' ? 'Jira Software' : 'Hybrid'}
                                     </div>
                                </div>
                                <div className="border rounded-md p-4">
                                     <div className="flex items-center gap-2 mb-2 font-semibold text-slate-700">
                                         <Settings className="w-4 h-4" /> Configuration Strategy
                                     </div>
                                     <div className="text-2xl font-bold text-blue-700 capitalize">
                                         {formatCategoryLabel(data.configStrategy)}
                                     </div>
                                </div>
                            </div>

                            <div className="border rounded-md p-4">
                                 <div className="flex items-center gap-2 mb-3 font-semibold text-slate-700">
                                     <FileCode className="w-4 h-4" /> Scope: Included Work Types
                                 </div>
                                 <div className="flex flex-wrap gap-2">
                                     {data.includedWorkTypes.map(wt => (
                                         <Badge key={wt} variant="secondary" className="px-3 py-1 text-sm bg-slate-100 text-slate-800">
                                             {wt}
                                         </Badge>
                                     ))}
                                 </div>
                            </div>

                            {/* Details Expandable */}
                            <div className="border-t pt-4">
                                <h4 className="font-medium mb-2">Decision Context</h4>
                                <ul className="list-disc pl-5 text-sm text-slate-600 space-y-1">
                                    <li>Audience: {data.audience.internal ? 'Internal' : ''} {data.audience.internal && data.audience.external ? '&' : ''} {data.audience.external ? 'External' : ''}</li>
                                    <li>Needs: {[
                                        data.needs.portal && 'Portal', 
                                        data.needs.slas && 'SLAs', 
                                        data.needs.incidents && 'Incidents', 
                                        data.needs.changes && 'Change Mgmt'
                                    ].filter(Boolean).join(', ') || 'Standard Issue Tracking'}</li>
                                </ul>
                            </div>
                        </CardContent>
                    </Card>
               </div>

               {/* Footer Actions */}
               <div className="p-4 bg-white border-t flex justify-between items-center sticky bottom-0 z-10">
                    <Button variant="ghost" onClick={onCancel}>Close</Button>
                    <div className="flex gap-2">
                         {!data.locked ? (
                             <>
                                <Button variant="outline" onClick={handleBack}>Back to Edit</Button>
                                <Button 
                                    className="bg-blue-600 hover:bg-blue-700"
                                    onClick={() => setShowCommitDialog(true)}
                                >
                                    Ready for Build
                                    <Shield className="w-4 h-4 ml-2" />
                                </Button>
                             </>
                         ) : (
                             <Button variant="outline" onClick={() => setShowUnlockDialog(true)}>
                                 Unlock / Edit
                                 <Lock className="w-4 h-4 ml-2" />
                             </Button>
                         )}
                    </div>
               </div>

               <CommitDialog 
                   open={showCommitDialog} 
                   onClose={() => setShowCommitDialog(false)} 
                   onConfirm={handleCommit} 
               />
               <UnlockDialog
                   open={showUnlockDialog}
                   onClose={() => setShowUnlockDialog(false)}
                   onUnlock={handleUnlock}
               />
          </div>
      );
  }

  // WIZARD STEPS
  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-y-auto p-6 max-w-3xl mx-auto w-full">
          {/* Progress Indicator */}
          <div className="flex items-center justify-between mb-8 px-2">
               {[1, 2, 3, 4].map(i => (
                   <div key={i} className={`flex items-center gap-2 ${step >= i ? 'text-blue-600' : 'text-slate-300'}`}>
                       <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold border-2 ${
                           step === i ? 'border-blue-600 bg-blue-50' : 
                           step > i ? 'border-blue-600 bg-blue-600 text-white' : 
                           'border-slate-200 bg-white'
                       }`}>
                           {step > i ? <Check className="w-5 h-5" /> : i}
                       </div>
                       <span className="hidden sm:inline font-medium text-sm">
                           {i === 1 && "Goals"}
                           {i === 2 && "Audience"}
                           {i === 3 && "Needs"}
                           {i === 4 && "Strategy"}
                       </span>
                   </div>
               ))}
          </div>

          <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
              
              {/* STEP 1 */}
              {step === 1 && (
                  <Card>
                      <CardHeader>
                          <CardTitle>What are we primarily implementing?</CardTitle>
                          <CardDescription>This determines the fundamental template we will use.</CardDescription>
                      </CardHeader>
                      <CardContent>
                          <RadioGroup value={goal} onValueChange={(v: any) => setGoal(v)} className="grid gap-4">
                              <Label htmlFor="goal-itsm" className="cursor-pointer border-2 rounded-lg p-4 hover:bg-slate-50 [&:has(:checked)]:border-blue-600 [&:has(:checked)]:bg-blue-50 transition-all">
                                  <RadioGroupItem value="itsm" id="goal-itsm" className="sr-only" />
                                  <div className="flex items-center gap-3">
                                      <div className="bg-blue-100 p-2 rounded-full"><Users className="w-5 h-5 text-blue-600" /></div>
                                      <div>
                                          <div className="font-semibold text-lg">IT Service Management (ITSM)</div>
                                          <div className="text-slate-500">Service Desk, Help Desk, Support Tickets</div>
                                      </div>
                                  </div>
                              </Label>
                              <Label htmlFor="goal-software" className="cursor-pointer border-2 rounded-lg p-4 hover:bg-slate-50 [&:has(:checked)]:border-blue-600 [&:has(:checked)]:bg-blue-50 transition-all">
                                  <RadioGroupItem value="software" id="goal-software" className="sr-only" />
                                  <div className="flex items-center gap-3">
                                      <div className="bg-green-100 p-2 rounded-full"><FileCode className="w-5 h-5 text-green-600" /></div>
                                      <div>
                                          <div className="font-semibold text-lg">Software / Work Tracking</div>
                                          <div className="text-slate-500">Development tasks, Project management, Backlogs</div>
                                      </div>
                                  </div>
                              </Label>
                              <Label htmlFor="goal-both" className="cursor-pointer border-2 rounded-lg p-4 hover:bg-slate-50 [&:has(:checked)]:border-blue-600 [&:has(:checked)]:bg-blue-50 transition-all">
                                  <RadioGroupItem value="both" id="goal-both" className="sr-only" />
                                  <div className="flex items-center gap-3">
                                      <div className="bg-purple-100 p-2 rounded-full"><LayoutTemplate className="w-5 h-5 text-purple-600" /></div>
                                      <div>
                                          <div className="font-semibold text-lg">Both (Hybrid)</div>
                                          <div className="text-slate-500">We need both Service Desk and Dev/Project tracking</div>
                                      </div>
                                  </div>
                              </Label>
                          </RadioGroup>
                      </CardContent>
                  </Card>
              )}

              {/* STEP 2 */}
              {step === 2 && (
                  <Card>
                      <CardHeader>
                          <CardTitle>Who will use this system?</CardTitle>
                          <CardDescription>Select all that apply.</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                          <div className={`flex items-start space-x-3 p-4 border rounded-lg cursor-pointer hover:bg-slate-50 transition-all ${audienceInternal ? 'border-blue-600 bg-blue-50' : ''}`} onClick={() => setAudienceInternal(!audienceInternal)}>
                              <Checkbox checked={audienceInternal} onCheckedChange={(c) => setAudienceInternal(!!c)} id="aud-int" />
                              <div className="grid gap-1.5 leading-none">
                                  <label htmlFor="aud-int" className="text-lg font-semibold leading-none cursor-pointer">
                                      Internal Employees
                                  </label>
                                  <p className="text-sm text-muted-foreground">
                                      Staff members who work within the organization.
                                  </p>
                              </div>
                          </div>
                          <div className={`flex items-start space-x-3 p-4 border rounded-lg cursor-pointer hover:bg-slate-50 transition-all ${audienceExternal ? 'border-blue-600 bg-blue-50' : ''}`} onClick={() => setAudienceExternal(!audienceExternal)}>
                              <Checkbox checked={audienceExternal} onCheckedChange={(c) => setAudienceExternal(!!c)} id="aud-ext" />
                              <div className="grid gap-1.5 leading-none">
                                  <label htmlFor="aud-ext" className="text-lg font-semibold leading-none cursor-pointer">
                                      External Customers
                                  </label>
                                  <p className="text-sm text-muted-foreground">
                                      Clients or customers outside the organization who submit requests.
                                  </p>
                              </div>
                          </div>
                      </CardContent>
                  </Card>
              )}

              {/* STEP 3 */}
              {step === 3 && (
                  <Card>
                      <CardHeader>
                          <CardTitle>Operational Needs</CardTitle>
                          <CardDescription>Which features are critical for launch?</CardDescription>
                      </CardHeader>
                      <CardContent className="grid gap-4">
                          <div className="flex items-center justify-between p-4 border rounded-lg">
                              <div className="space-y-1">
                                  <div className="font-semibold flex items-center gap-2">
                                      <Globe className="w-4 h-4 text-slate-500" /> Customer Portal
                                  </div>
                                  <div className="text-sm text-slate-500">A simplified website for users to submit tickets.</div>
                              </div>
                              <Checkbox checked={needPortal} onCheckedChange={(c) => setNeedPortal(!!c)} className="w-6 h-6" />
                          </div>

                          <div className="flex items-center justify-between p-4 border rounded-lg">
                              <div className="space-y-1">
                                  <div className="font-semibold flex items-center gap-2">
                                      <Activity className="w-4 h-4 text-slate-500" /> SLAs
                                  </div>
                                  <div className="text-sm text-slate-500">Service Level Agreements (timers) for response/resolution.</div>
                              </div>
                              <Checkbox checked={needSlas} onCheckedChange={(c) => setNeedSlas(!!c)} className="w-6 h-6" />
                          </div>

                          <div className="flex items-center justify-between p-4 border rounded-lg">
                              <div className="space-y-1">
                                  <div className="font-semibold flex items-center gap-2">
                                      <AlertTriangle className="w-4 h-4 text-slate-500" /> Incident Management
                                  </div>
                                  <div className="text-sm text-slate-500">Major incident handling, on-call schedules, responders.</div>
                              </div>
                              <Checkbox checked={needIncidents} onCheckedChange={(c) => setNeedIncidents(!!c)} className="w-6 h-6" />
                          </div>

                          <div className="flex items-center justify-between p-4 border rounded-lg">
                              <div className="space-y-1">
                                  <div className="font-semibold flex items-center gap-2">
                                      <Shield className="w-4 h-4 text-slate-500" /> Change Management
                                  </div>
                                  <div className="text-sm text-slate-500">Formal approvals (CAB), risk assessment, change calendars.</div>
                              </div>
                              <Checkbox checked={needChanges} onCheckedChange={(c) => setNeedChanges(!!c)} className="w-6 h-6" />
                          </div>
                      </CardContent>
                  </Card>
              )}

              {/* STEP 4 */}
              {step === 4 && (
                  <Card>
                      <CardHeader>
                          <CardTitle>Configuration Strategy</CardTitle>
                          <CardDescription>
                              How standardized should the setup be across different teams?
                          </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-6">
                          <Alert className="bg-blue-50 border-blue-200">
                              <Info className="h-4 w-4 text-blue-600" />
                              <AlertDescription className="text-blue-800 text-sm">
                                  <strong>Shared Configuration</strong> = Faster setup, easier maintenance.<br/>
                                  <strong>Dedicated Configuration</strong> = More flexibility, higher maintenance.
                              </AlertDescription>
                          </Alert>

                          <div className="space-y-3">
                              <Label className="text-base">Do you have multiple teams that must operate differently?</Label>
                              <RadioGroup value={multiTeamDiff} onValueChange={(v: any) => setMultiTeamDiff(v)}>
                                  <div className="flex items-center space-x-2">
                                      <RadioGroupItem value="no" id="r-no" />
                                      <Label htmlFor="r-no" className="font-normal">No — keep it simple and consistent (Recommended)</Label>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                      <RadioGroupItem value="yes" id="r-yes" />
                                      <Label htmlFor="r-yes" className="font-normal">Yes — teams differ meaningfully in workflow/fields</Label>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                      <RadioGroupItem value="unsure" id="r-unsure" />
                                      <Label htmlFor="r-unsure" className="font-normal">Not sure / Maybe later</Label>
                                  </div>
                              </RadioGroup>
                          </div>
                      </CardContent>
                  </Card>
              )}

          </div>
      </div>

      <div className="p-4 bg-white border-t flex justify-between items-center sticky bottom-0 z-10">
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <div className="flex gap-2">
              {step > 1 && <Button variant="outline" onClick={handleBack}>Back</Button>}
              <Button onClick={handleNext} disabled={
                  (step === 1 && !canProceedStep1) || 
                  (step === 2 && !canProceedStep2) ||
                  (step === 3 && !canProceedStep3) ||
                  (step === 4 && !canProceedStep4)
              }>
                  {step === 4 ? 'Review Recommendation' : 'Next'}
                  <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
          </div>
      </div>
    </div>
  );
}

// --- DIALOGS ---

function CommitDialog({ open, onClose, onConfirm }: { open: boolean, onClose: () => void, onConfirm: (name: string) => void }) {
    const [agreed, setAgreed] = useState(false);
    const [name, setName] = useState('');

    useEffect(() => { if (open) { setAgreed(false); setName(''); }}, [open]);

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Confirm Space Architecture</DialogTitle>
                    <DialogDescription>
                        This decision locks the architectural approach for this engagement.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                     <Alert className="bg-yellow-50 border-yellow-200">
                        <AlertTriangle className="h-4 w-4 text-yellow-600" />
                        <AlertDescription className="text-yellow-800 text-sm">
                            Changes after this point cause re-work and may require a Change Order.
                        </AlertDescription>
                    </Alert>
                    <div className="flex items-start gap-2 p-3 border rounded-md">
                         <Checkbox id="agree" checked={agreed} onCheckedChange={(c) => setAgreed(!!c)} />
                         <Label htmlFor="agree" className="text-sm font-medium leading-tight pt-0.5">
                             I understand and agree that this locks the architecture.
                         </Label>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="signer">Type full name to confirm</Label>
                        <Input id="signer" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. John Doe" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button disabled={!agreed || !name.trim()} onClick={() => onConfirm(name)}>Confirm & Lock</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function UnlockDialog({ open, onClose, onUnlock }: { open: boolean, onClose: () => void, onUnlock: (type: 'defect'|'scope_change', reason: string) => void }) {
    const [type, setType] = useState<'defect'|'scope_change'>('defect');
    const [reason, setReason] = useState('');

    useEffect(() => { if (open) { setType('defect'); setReason(''); }}, [open]);

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Unlock Architecture</DialogTitle>
                    <DialogDescription>Why are you modifying the locked architecture?</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                    <RadioGroup value={type} onValueChange={(v: any) => setType(v)} className="grid grid-cols-2 gap-4">
                        <div className={`border p-3 rounded cursor-pointer ${type==='defect'?'bg-slate-100 border-slate-400':''}`} onClick={()=>setType('defect')}>
                            <div className="font-semibold flex items-center gap-2"><Bug className="w-4 h-4"/> Defect</div>
                            <div className="text-xs text-slate-500">Correction to meet orig. requirements</div>
                        </div>
                        <div className={`border p-3 rounded cursor-pointer ${type==='scope_change'?'bg-slate-100 border-slate-400':''}`} onClick={()=>setType('scope_change')}>
                            <div className="font-semibold flex items-center gap-2"><FileText className="w-4 h-4"/> Scope Change</div>
                            <div className="text-xs text-slate-500">New/Changed requirements</div>
                        </div>
                    </RadioGroup>
                    <div className="space-y-2">
                        <Label>Reason</Label>
                        <Textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Explain why..." />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button disabled={!reason.trim()} onClick={() => onUnlock(type, reason)}>Unlock</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}